import React from 'react';
import { Link } from 'react-router-dom';
import {
  Wallet, Coins, CircleDollarSign, TrendingUp, ArrowDownToLine, Banknote, Bot,
} from 'lucide-react';
import { StatCard, Panel, Empty, StatusBadge, Disclaimer } from '../../components/Primitives';
import { Sparkline } from '../../components/Charts';
import { useFetch } from '../../lib/hooks';
import { fmtUsd, fmtDate, api } from '../../lib/api';
import { useI18n } from '../../i18n/I18nContext';
import { TodayTrading, TradingResults } from '../../components/TradingResults';

export default function Overview() {
  const { t } = useI18n();
  const { data: summary } = useFetch('/account/summary', [], 20000);
  const { data: deposits } = useFetch('/deposits');
  const { data: ledger } = useFetch('/account/ledger');
  const { data: bot } = useFetch('/bot/status', [], 30000);
  const [markets, setMarkets] = React.useState([]);
  React.useEffect(() => { api.get('/markets/top?limit=5').then(({ data }) => setMarkets(data.coins)).catch(() => {}); }, []);

  const s = summary || {};
  const pnl = Number(s.realized_pnl || 0);

  return (
    <div className="space-y-6" data-testid="dashboard-overview">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        <StatCard testId="stat-deposits" label={t('dash.totalDeposits')} value={fmtUsd(s.total_deposits)} accent="silver" icon={ArrowDownToLine} />
        <StatCard testId="stat-available" label={t('dash.availableBalance')} value={fmtUsd(s.available_balance)} accent="neon" icon={Wallet} />
        <StatCard testId="stat-allocation" label={t('dash.allocation')} value={fmtUsd(s.allocation)} accent="gold" icon={Coins} />
        <StatCard testId="stat-pnl" label={t('dash.realizedPnl')} value={fmtUsd(s.realized_pnl)} accent={pnl >= 0 ? 'neon' : 'red'} icon={TrendingUp} />
        <StatCard testId="stat-withdrawable" label={t('dash.withdrawable')} value={fmtUsd(s.withdrawable)} accent="emerald" icon={Banknote} />
        <StatCard testId="stat-equity" label={t('dash.netEquity')} value={fmtUsd(s.net_equity)} accent="silver" icon={CircleDollarSign} />
      </div>

      <TodayTrading bot={bot} />

      <div className="grid gap-6 lg:grid-cols-3">
        <Panel title="S.I. Bot Status" className="lg:col-span-1" testId="overview-bot">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full border border-vj-neon/40 grid place-items-center vj-pulse">
              <Bot size={24} className="text-vj-neon" />
            </div>
            <div>
              <StatusBadge status={bot?.active ? 'healthy' : 'paused'} />
              <div className="font-display text-lg mt-1">{bot?.status || 'Offline'}</div>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
            <div><div className="text-xs text-vj-grey">Mode</div><div className="font-mono">{(bot?.mode || 'paper').toUpperCase()}</div></div>
            <div><div className="text-xs text-vj-grey">Trades</div><div className="font-mono">{bot?.executed_trades ?? 0}</div></div>
            <div><div className="text-xs text-vj-grey">Realized P&L</div><div className={`font-mono ${Number(bot?.realized_pnl) >= 0 ? 'text-vj-neon' : 'text-vj-red'}`}>{fmtUsd(bot?.realized_pnl)}</div></div>
            <div><div className="text-xs text-vj-grey">Fees</div><div className="font-mono">{fmtUsd(bot?.fees)}</div></div>
          </div>
          <Link to="/dashboard/si-bot" className="mt-4 inline-block text-xs text-vj-neon">Open S.I. Bot →</Link>
        </Panel>

        <Panel title="Market Watchlist" className="lg:col-span-2" testId="overview-markets">
          <div className="space-y-2">
            {markets.map((c) => {
              const up = (c.price_change_percentage_24h || 0) >= 0;
              return (
                <div key={c.id} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                  <div className="flex items-center gap-3">
                    <img src={c.image} alt="" className="w-6 h-6" />
                    <div><div className="text-sm font-medium">{c.symbol}</div><div className="text-xs text-vj-grey">{c.name}</div></div>
                  </div>
                  <Sparkline data={c.sparkline} positive={up} width={90} height={26} />
                  <div className="text-right">
                    <div className="font-mono text-sm">{fmtUsd(c.current_price, c.current_price < 1 ? 4 : 2)}</div>
                    <div className={`text-xs font-mono ${up ? 'text-vj-neon' : 'text-vj-red'}`}>{up ? '+' : ''}{(c.price_change_percentage_24h || 0).toFixed(2)}%</div>
                  </div>
                </div>
              );
            })}
          </div>
        </Panel>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Panel title="Recent Transactions" testId="overview-ledger">
          {ledger && ledger.length ? (
            <div className="space-y-2">
              {ledger.slice(0, 6).map((l) => (
                <div key={l.id} className="flex items-center justify-between text-sm py-1.5 border-b border-border last:border-0">
                  <span className="capitalize">{l.type.replace(/_/g, ' ')}</span>
                  <span className={`font-mono ${Number(l.amount) >= 0 ? 'text-vj-neon' : 'text-vj-red'}`}>{Number(l.amount) >= 0 ? '+' : ''}{fmtUsd(l.amount)}</span>
                  <span className="text-xs text-vj-grey">{fmtDate(l.created_at)}</span>
                </div>
              ))}
            </div>
          ) : <Empty>No ledger entries yet. Make a deposit to begin.</Empty>}
        </Panel>
        <Panel title="Recent Deposits" testId="overview-deposits">
          {deposits && deposits.length ? (
            <div className="space-y-2">
              {deposits.slice(0, 6).map((d) => (
                <div key={d.id} className="flex items-center justify-between text-sm py-1.5 border-b border-border last:border-0">
                  <span className="font-mono">{fmtUsd(d.amount)} USDT</span>
                  <StatusBadge status={d.status} />
                  <span className="text-xs text-vj-grey">{d.confirmations >= d.required_confirmations ? `${d.required_confirmations}+` : d.confirmations}/{d.required_confirmations} conf</span>
                </div>
              ))}
            </div>
          ) : <Empty>No deposits yet. <Link to="/dashboard/deposits" className="text-vj-neon">Deposit USDT →</Link></Empty>}
        </Panel>
      </div>

      <TradingResults limit={5} />

      <Disclaimer>Metrics are computed from the authoritative backend ledger. S.I. Bot activity is paper trading / market monitoring — not real exchange executions.</Disclaimer>
    </div>
  );
}
